import type { FormEvent } from 'react';
import { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { useData } from '@/context/DataContext';
import { supabase } from '@/lib/supabase';
import Modal from '@/components/base/Modal';
import { Field, Input, Select, Textarea } from '@/components/base/FormField';
import {
  projectCategories,
  sectors,
  taskStatuses,
  taskPriorities,
  paymentTypes,
  purchaseMaterialStatuses,
  purchasePaymentStatuses,
  quoteStatuses,
  projectStatuses,
  serviceStatuses,
} from '@/types';

const cancelClass =
  'cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100';
const saveClass =
  'cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60';

const team = ['Fatih Demir', 'Emre Arslan', 'Ahmet Yıldız', 'Mehmet Kaya', 'Cem Özkan'];

export default function EditModals() {
  const { editTarget, closeEdit, showToast } = useApp();
  const { customers, projects, refresh, projectById } = useData();
  const [busy, setBusy] = useState(false);

  const rec = (editTarget?.record ?? {}) as Record<string, unknown>;
  const s = (k: string) => String(rec?.[k] ?? '');

  const orNull = (v: string) => (v === '' ? null : v);

  const handle =
    (table: string, id: string, build: (get: (n: string) => string, getNum: (n: string) => number) => Record<string, unknown>, message: string) =>
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const fd = new FormData(e.currentTarget);
      const get = (n: string) => String(fd.get(n) ?? '').trim();
      const getNum = (n: string) => Number(fd.get(n) ?? 0) || 0;
      const payload = build(get, getNum);
      setBusy(true);
      const { error } = await supabase.from(table).update(payload).eq('id', id);
      setBusy(false);
      if (error) {
        showToast('Güncellenemedi: ' + error.message);
      } else {
        showToast(message);
        closeEdit();
        refresh();
      }
    };

  const customerOptions = customers.map((c) => (
    <option key={c.id} value={c.id}>
      {c.name}
    </option>
  ));
  const projectOptions = projects.map((p) => (
    <option key={p.id} value={p.id}>
      {p.name}
    </option>
  ));

  return (
    <>
      <Modal open={editTarget?.type === 'customer'} onClose={closeEdit} title="Müşteriyi Düzenle" subtitle="Bilgileri güncelleyin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_customers',
            s('id'),
            (get) => ({
              name: get('name'),
              contact_person: get('contactPerson'),
              phone: get('phone'),
              email: get('email'),
              city: get('city'),
              sector: get('sector'),
              address: get('address'),
              notes: get('notes'),
            }),
            'Müşteri güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Firma Adı" required><Input name="name" required defaultValue={s('name')} placeholder="Firma unvanı" /></Field>
            <Field label="Yetkili Kişi" required><Input name="contactPerson" required defaultValue={s('contactPerson')} placeholder="Ad Soyad" /></Field>
            <Field label="Telefon"><Input name="phone" defaultValue={s('phone')} placeholder="05xx xxx xx xx" /></Field>
            <Field label="E-posta"><Input name="email" type="email" defaultValue={s('email')} placeholder="ornek@firma.com" /></Field>
            <Field label="Şehir"><Input name="city" defaultValue={s('city')} placeholder="İl" /></Field>
            <Field label="Sektör"><Select name="sector" defaultValue={s('sector')}>{sectors.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label="Adres" className="sm:col-span-2"><Input name="address" defaultValue={s('address')} placeholder="Açık adres" /></Field>
            <Field label="Notlar" className="sm:col-span-2"><Textarea name="notes" defaultValue={s('notes')} placeholder="Ek notlar" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'quote'} onClose={closeEdit} title="Teklifi Düzenle" subtitle="Teklif bilgilerini güncelleyin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_quotes',
            s('id'),
            (get, getNum) => {
              const amount = getNum('amount');
              const estimatedCost = getNum('estimatedCost');
              return {
                customer_id: orNull(get('customerId')),
                project_name: get('projectName'),
                category: get('category'),
                date: orNull(get('date')),
                validity_date: orNull(get('validityDate')),
                amount,
                estimated_cost: estimatedCost,
                estimated_profit: amount - estimatedCost,
                status: get('status'),
                description: get('description'),
                tax_status: get('taxStatus'),
                payment_plan: get('paymentPlan'),
                delivery_time: get('deliveryTime'),
                technical_notes: get('technicalNotes'),
                internal_note: get('internalNote'),
              };
            },
            'Teklif güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Müşteri" required><Select name="customerId" required defaultValue={s('customerId')}><option value="">Seçiniz</option>{customerOptions}</Select></Field>
            <Field label="Proje Adı" required><Input name="projectName" required defaultValue={s('projectName')} placeholder="Proje / iş adı" /></Field>
            <Field label="İş Kategorisi"><Select name="category" defaultValue={s('category')}>{projectCategories.map((c) => <option key={c}>{c}</option>)}</Select></Field>
            <Field label="Durum"><Select name="status" defaultValue={s('status')}>{quoteStatuses.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label="Teklif Tarihi"><Input name="date" type="date" defaultValue={s('date')} /></Field>
            <Field label="Geçerlilik Tarihi"><Input name="validityDate" type="date" defaultValue={s('validityDate')} /></Field>
            <Field label="Teklif Tutarı (₺)"><Input name="amount" type="number" defaultValue={s('amount')} placeholder="0" /></Field>
            <Field label="Vergi Durumu"><Select name="taxStatus" defaultValue={s('taxStatus') || 'KDV Dahil'}><option>KDV Dahil</option><option>KDV Hariç</option><option>Muaf</option></Select></Field>
            <Field label="Tahmini Maliyet (₺)"><Input name="estimatedCost" type="number" defaultValue={s('estimatedCost')} placeholder="0" /></Field>
            <Field label="Ödeme Planı" className="sm:col-span-2"><Input name="paymentPlan" defaultValue={s('paymentPlan')} placeholder="Örn: %30 kapora, %40 ara ödeme, %30 teslim" /></Field>
            <Field label="Teslim Süresi"><Input name="deliveryTime" defaultValue={s('deliveryTime')} placeholder="Örn: 6 hafta" /></Field>
            <Field label="Talep Açıklaması" className="sm:col-span-2"><Textarea name="description" defaultValue={s('description')} placeholder="Müşteri talebi" /></Field>
            <Field label="Teknik Açıklamalar" className="sm:col-span-2"><Textarea name="technicalNotes" defaultValue={s('technicalNotes')} placeholder="Teknik detaylar" /></Field>
            <Field label="İç Not" className="sm:col-span-2"><Textarea name="internalNote" defaultValue={s('internalNote')} placeholder="Sadece ekip içi not" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'project'} onClose={closeEdit} title="Projeyi Düzenle" subtitle="Proje bilgilerini güncelleyin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_projects',
            s('id'),
            (get, getNum) => ({
              name: get('name'),
              customer_id: orNull(get('customerId')),
              category: get('category'),
              responsible: get('responsible'),
              start_date: orNull(get('startDate')),
              planned_delivery: orNull(get('plannedDelivery')),
              sales_amount: getNum('salesAmount'),
              status: get('status'),
              description: get('description'),
            }),
            'Proje güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Proje Adı" required><Input name="name" required defaultValue={s('name')} placeholder="Proje adı" /></Field>
            <Field label="Müşteri" required><Select name="customerId" required defaultValue={s('customerId')}><option value="">Seçiniz</option>{customerOptions}</Select></Field>
            <Field label="Proje Kategorisi"><Select name="category" defaultValue={s('category')}>{projectCategories.map((c) => <option key={c}>{c}</option>)}</Select></Field>
            <Field label="Proje Sorumlusu"><Select name="responsible" defaultValue={s('responsible')}><option value="">Seçiniz</option>{team.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Durum"><Select name="status" defaultValue={s('status')}>{projectStatuses.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label="Başlangıç Tarihi"><Input name="startDate" type="date" defaultValue={s('startDate')} /></Field>
            <Field label="Planlanan Teslim"><Input name="plannedDelivery" type="date" defaultValue={s('plannedDelivery')} /></Field>
            <Field label="Satış Tutarı (₺)"><Input name="salesAmount" type="number" defaultValue={s('salesAmount')} placeholder="0" /></Field>
            <Field label="Açıklama" className="sm:col-span-2"><Textarea name="description" defaultValue={s('description')} placeholder="Proje kapsamı" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'task'} onClose={closeEdit} title="Görevi Düzenle" subtitle="Görev bilgilerini güncelleyin">
        <form
          onSubmit={handle(
            '3dmachine_tasks',
            s('id'),
            (get) => {
              const pid = get('projectId');
              const customerId = pid ? projectById(pid)?.customerId ?? null : null;
              return {
                title: get('title'),
                customer_id: customerId,
                project_id: orNull(pid),
                assignee: get('assignee'),
                start_date: orNull(get('startDate')),
                due_date: orNull(get('dueDate')),
                priority: get('priority') || 'Normal',
                status: get('status') || 'Yapılacak',
              };
            },
            'Görev güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Görev" required><Input name="title" required defaultValue={s('title')} placeholder="Görev tanımı" /></Field>
            <Field label="Sorumlu"><Select name="assignee" defaultValue={s('assignee')}><option value="">Seçiniz</option>{team.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Bağlı Proje"><Select name="projectId" defaultValue={s('projectId')}><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Öncelik"><Select name="priority" defaultValue={s('priority')}>{taskPriorities.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Başlangıç"><Input name="startDate" type="date" defaultValue={s('startDate')} /></Field>
            <Field label="Son Tarih"><Input name="dueDate" type="date" defaultValue={s('dueDate')} /></Field>
            <Field label="Durum"><Select name="status" defaultValue={s('status')}>{taskStatuses.map((x) => <option key={x}>{x}</option>)}</Select></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'collection'} onClose={closeEdit} title="Tahsilatı Düzenle" subtitle="Tahsilat kaydını güncelleyin">
        <form
          onSubmit={handle(
            '3dmachine_collections',
            s('id'),
            (get, getNum) => ({
              date: orNull(get('date')),
              customer_id: orNull(get('customerId')),
              project_id: orNull(get('projectId')),
              payment_type: get('paymentType'),
              amount: getNum('amount'),
              description: get('description'),
            }),
            'Tahsilat güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tarih"><Input name="date" type="date" defaultValue={s('date')} /></Field>
            <Field label="Müşteri" required><Select name="customerId" required defaultValue={s('customerId')}><option value="">Seçiniz</option>{customerOptions}</Select></Field>
            <Field label="Proje"><Select name="projectId" defaultValue={s('projectId')}><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Ödeme Tipi"><Select name="paymentType" defaultValue={s('paymentType')}>{paymentTypes.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Tutar (₺)" required><Input name="amount" required type="number" defaultValue={s('amount')} placeholder="0" /></Field>
            <Field label="Açıklama"><Input name="description" defaultValue={s('description')} placeholder="Açıklama" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'payment'} onClose={closeEdit} title="Ödemeyi Düzenle" subtitle="Ödeme kaydını güncelleyin">
        <form
          onSubmit={handle(
            '3dmachine_payments',
            s('id'),
            (get, getNum) => ({
              date: orNull(get('date')),
              supplier: get('supplier'),
              project_id: orNull(get('projectId')),
              category: get('category'),
              amount: getNum('amount'),
              description: get('description'),
            }),
            'Ödeme güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Tarih"><Input name="date" type="date" defaultValue={s('date')} /></Field>
            <Field label="Tedarikçi" required><Input name="supplier" required defaultValue={s('supplier')} placeholder="Tedarikçi adı" /></Field>
            <Field label="Proje"><Select name="projectId" defaultValue={s('projectId')}><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Kategori"><Select name="category" defaultValue={s('category')}><option>Malzeme</option><option>İşçilik</option><option>Nakliye</option><option>Genel Gider</option></Select></Field>
            <Field label="Tutar (₺)" required><Input name="amount" required type="number" defaultValue={s('amount')} placeholder="0" /></Field>
            <Field label="Açıklama"><Input name="description" defaultValue={s('description')} placeholder="Açıklama" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'purchase'} onClose={closeEdit} title="Satın Almayı Düzenle" subtitle="Satın alma kaydını güncelleyin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_purchases',
            s('id'),
            (get, getNum) => {
              const quantity = getNum('quantity') || 1;
              const unitPrice = getNum('unitPrice');
              return {
                project_id: orNull(get('projectId')),
                supplier: get('supplier'),
                material: get('material'),
                brand_model: get('brandModel'),
                quantity,
                unit_price: unitPrice,
                total: quantity * unitPrice,
                order_date: orNull(get('orderDate')),
                expected_delivery: orNull(get('expectedDelivery')),
                payment_status: get('paymentStatus'),
                material_status: get('materialStatus'),
              };
            },
            'Satın alma güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Proje" required><Select name="projectId" required defaultValue={s('projectId')}><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Tedarikçi" required><Input name="supplier" required defaultValue={s('supplier')} placeholder="Tedarikçi adı" /></Field>
            <Field label="Malzeme / Ürün" required><Input name="material" required defaultValue={s('material')} placeholder="Malzeme adı" /></Field>
            <Field label="Marka / Model"><Input name="brandModel" defaultValue={s('brandModel')} placeholder="Marka ve model" /></Field>
            <Field label="Adet"><Input name="quantity" type="number" defaultValue={s('quantity')} placeholder="1" /></Field>
            <Field label="Birim Fiyat (₺)"><Input name="unitPrice" type="number" defaultValue={s('unitPrice')} placeholder="0" /></Field>
            <Field label="Sipariş Tarihi"><Input name="orderDate" type="date" defaultValue={s('orderDate')} /></Field>
            <Field label="Beklenen Teslim"><Input name="expectedDelivery" type="date" defaultValue={s('expectedDelivery')} /></Field>
            <Field label="Ödeme Durumu"><Select name="paymentStatus" defaultValue={s('paymentStatus')}>{purchasePaymentStatuses.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label="Malzeme Durumu"><Select name="materialStatus" defaultValue={s('materialStatus')}>{purchaseMaterialStatuses.map((x) => <option key={x}>{x}</option>)}</Select></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>

      <Modal open={editTarget?.type === 'service'} onClose={closeEdit} title="Servis Kaydını Düzenle" subtitle="Servis / revizyon kaydını güncelleyin" size="lg">
        <form
          onSubmit={handle(
            '3dmachine_service_records',
            s('id'),
            (get, getNum) => ({
              customer_id: orNull(get('customerId')),
              project_id: orNull(get('projectId')),
              request_date: orNull(get('requestDate')),
              issue: get('issue'),
              priority: get('priority') || 'Normal',
              assignee: get('assignee'),
              planned_date: orNull(get('plannedDate')),
              warranty: get('warranty') || 'Garanti',
              fee: getNum('fee'),
              status: get('status'),
              result: get('result'),
            }),
            'Servis kaydı güncellendi',
          )}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Müşteri" required><Select name="customerId" required defaultValue={s('customerId')}><option value="">Seçiniz</option>{customerOptions}</Select></Field>
            <Field label="Makine / Proje"><Select name="projectId" defaultValue={s('projectId')}><option value="">Seçiniz</option>{projectOptions}</Select></Field>
            <Field label="Talep Tarihi"><Input name="requestDate" type="date" defaultValue={s('requestDate')} /></Field>
            <Field label="Öncelik"><Select name="priority" defaultValue={s('priority')}>{taskPriorities.map((p) => <option key={p}>{p}</option>)}</Select></Field>
            <Field label="Sorumlu"><Select name="assignee" defaultValue={s('assignee')}><option value="">Seçiniz</option>{team.map((t) => <option key={t}>{t}</option>)}</Select></Field>
            <Field label="Planlanan Servis Tarihi"><Input name="plannedDate" type="date" defaultValue={s('plannedDate')} /></Field>
            <Field label="Garanti Durumu"><Select name="warranty" defaultValue={s('warranty') || 'Garanti'}><option>Garanti</option><option>Ücretli</option></Select></Field>
            <Field label="Servis Bedeli (₺)"><Input name="fee" type="number" defaultValue={s('fee')} placeholder="0" /></Field>
            <Field label="Durum"><Select name="status" defaultValue={s('status')}>{serviceStatuses.map((x) => <option key={x}>{x}</option>)}</Select></Field>
            <Field label="Sorun" className="sm:col-span-2"><Textarea name="issue" defaultValue={s('issue')} placeholder="Arıza / talep açıklaması" /></Field>
            <Field label="Sonuç" className="sm:col-span-2"><Textarea name="result" defaultValue={s('result')} placeholder="Servis sonucu" /></Field>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={closeEdit} className={cancelClass}>Vazgeç</button>
            <button type="submit" disabled={busy} className={saveClass}>Kaydet</button>
          </div>
        </form>
      </Modal>
    </>
  );
}